import { apiService, resolveLoginUser } from "../services/api";
import {
  generateUniqueFormId,
  resolveUserCode as resolveSessionUserCode,
} from "../state/utils/configUtils";

export type BranchRecord = {
  id: string;
  name: string;
  mobile: string;
  soft_delete: number;
  locked?: number;
  status: "Active" | "Inactive";
};

export type BranchPayload = {
  name: string;
  mobile: string;
};

type ModuleSaveResponse = {
  success?: boolean;
  message?: string;
  itemid?: string;
  data?: Record<string, unknown> | null;
};

/** branches.id is varchar(100); keep generated ids under that limit. */
const BRANCH_ID_MAX_LEN = 100;
/** guid(32) + _ + code + _ + ts(15) + _ + inst(<=5) → code room ≈ 45; use 20 for margin. */
const BRANCH_ID_USERCODE_MAX = 20;

function toStatus(softDelete: unknown): "Active" | "Inactive" {
  const n = Number(softDelete ?? 0);
  return n === 0 ? "Active" : "Inactive";
}

function normalizeBranch(raw: Record<string, unknown> | null | undefined): BranchRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const id = raw.id != null ? String(raw.id).trim() : "";
  if (!id) return null;
  const softDelete = Number(raw.soft_delete ?? raw.softDelete ?? 0);
  return {
    id,
    name: String(raw.name ?? ""),
    mobile: String(raw.mobile ?? ""),
    soft_delete: softDelete,
    locked: raw.locked != null ? Number(raw.locked) : undefined,
    status: toStatus(softDelete),
  };
}

/**
 * Short code for id generation — never use full userid (often 60+ chars),
 * which makes generateUniqueFormId exceed branches.id varchar(100).
 */
function resolveBranchIdUserCode(): string {
  const loginUser = resolveLoginUser() as Record<string, unknown> | undefined;
  const fromSession = resolveSessionUserCode(loginUser);
  const candidates = [
    fromSession,
    loginUser?.usercode,
    loginUser?.userCode,
    loginUser?.empCode,
    loginUser?.loginName,
    loginUser?.username,
    loginUser?.userName,
  ];
  for (const value of candidates) {
    const s = String(value ?? "")
      .trim()
      .replace(/\s+/g, "")
      .toUpperCase();
    // Prefer short codes; skip UUID / composite form ids.
    if (s && s.length <= BRANCH_ID_USERCODE_MAX && !s.includes("-")) {
      return s;
    }
  }
  return "USR";
}

function generateBranchId(): string {
  const id = generateUniqueFormId(
    resolveBranchIdUserCode(),
    (Date.now() % 100000) || 1
  );
  if (id.length <= BRANCH_ID_MAX_LEN) return id;
  return id.slice(0, BRANCH_ID_MAX_LEN);
}

function resolveUserId(): string {
  const loginUser = resolveLoginUser() as Record<string, unknown> | undefined;
  if (!loginUser) return "system";
  const candidates = [
    loginUser.userid,
    loginUser.userId,
    loginUser.id,
    loginUser.username,
  ];
  for (const value of candidates) {
    const s = String(value ?? "").trim();
    if (s) return s;
  }
  return "system";
}

/** List branches for Settings (active rows from branch_summary). */
export async function listBranches(): Promise<BranchRecord[]> {
  const response = await apiService.getModuleData(
    "branch_summary",
    {
      skip: 0,
      limit: 500,
      search: [],
      // Backend GetSortField expects `value` (not `direction`).
      sort: [{ field: "name", value: "asc" }],
    },
    { includeAuthToken: true, errorHandling: "throw" }
  );

  if (!response?.success && !Array.isArray(response?.data)) {
    throw new Error("Failed to load branches.");
  }

  const rows = Array.isArray(response.data) ? response.data : [];
  return rows
    .map((row) => normalizeBranch(row as Record<string, unknown>))
    .filter((row): row is BranchRecord => row != null);
}

/**
 * Create or update a branch via the same module save path as screen 170.
 * Create: PUT /pabws/module/branch_add → InsertModuleData → Pab branch_add seed.
 * Update: POST /pabws/module/branch_add/update → UpdateModuleData.
 */
export async function saveBranch(
  payload: BranchPayload,
  options?: { id?: string; isEdit?: boolean }
): Promise<BranchRecord> {
  const name = String(payload.name || "").trim();
  const mobile = String(payload.mobile || "").trim();
  if (!name) {
    throw new Error("Branch name is required.");
  }
  if (!mobile) {
    throw new Error("Mobile number is required.");
  }

  const isEdit = Boolean(options?.isEdit && options?.id);
  const recordId = isEdit ? String(options!.id).trim() : generateBranchId();
  const nowIso = new Date().toISOString();
  const userId = resolveUserId();
  const loginUser = resolveLoginUser();

  const data: Record<string, unknown> = {
    id: recordId,
    name,
    mobile,
    subform: [],
  };

  if (isEdit) {
    data.modified_by = userId;
    data.modified_at = nowIso;
  } else {
    data.created_by = userId;
    data.created_at = nowIso;
    // Self-scope the row so stamp does not pin it to the login branch.
    data.transaction_branch_id = recordId;
  }

  const body = {
    data,
    next_action: "",
    next_state: "",
    ...(loginUser ? { loginUser } : {}),
  };

  const endpoint = "/pabws/module/branch_add";
  const response = (
    isEdit
      ? await apiService.post(`${endpoint}/update`, body, {
          includeAuthToken: true,
          errorHandling: "throw",
        })
      : await apiService.put(endpoint, body, {
          includeAuthToken: true,
          errorHandling: "throw",
        })
  ) as ModuleSaveResponse;

  // List POST also returns success:true — require an explicit insert/update success.
  if (response?.success !== true) {
    throw new Error(response?.message || "Failed to save branch.");
  }

  const savedId =
    (response.itemid != null && String(response.itemid).trim()) || recordId;

  return {
    id: savedId,
    name,
    mobile,
    soft_delete: 0,
    status: "Active",
  };
}
